import { useMemo } from 'react';
import { Platform, StyleSheet, TextStyle } from 'react-native';
import { useScale } from '../../hooks/useScale';
import { useColors } from '../../hooks/useColors';
import theme from '../../common/theme';

// `outlineStyle: 'none'` is a react-native-web value that RN's own TextStyle
// does not admit (it only declares 'solid' | 'dotted' | 'dashed'). Without it
// the browser draws its own focus ring inside the blue border InputText already
// renders on focus, showing two nested rectangles. The native targets run the
// old architecture (Android `newArchEnabled=false`, iOS pods with
// `RCT_NEW_ARCH_ENABLED=0`), which does not understand the value, so it is only
// spread in on web.
type WebTextStyle = Omit<TextStyle, 'outlineStyle'> & {
  outlineStyle?: TextStyle['outlineStyle'] | 'none';
};

const WEB_NO_FOCUS_RING: WebTextStyle = { outlineStyle: 'none' };

const InputTextStyles = () => {
  const { scale } = useScale();
  const { colors } = useColors();

  return useMemo(
    () =>
      StyleSheet.create({
        inputWrapper: {
          height: scale(44),
          width: '100%',
          alignItems: 'center',
          flexDirection: 'row',
          borderRadius: 6,
        },
        inputContainer: {
          flex: 1,
          width: '100%',
          height: '100%',
          fontSize: scale(14),
          paddingHorizontal: scale(10),
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
          color: colors.text,
          // The single assertion is only needed because spreading a wider
          // `outlineStyle` widens the whole style object past what
          // `StyleSheet.create` accepts; the value itself is checked above.
          ...(Platform.OS === 'web' ? (WEB_NO_FOCUS_RING as TextStyle) : null),
        },
        inputTitleText: {
          fontSize: scale(14),
          lineHeight: scale(18),
          marginBottom: scale(6),
          paddingLeft: scale(12),
          color: colors.grey6,
        },
        labelContainer: {
          height: '100%',
          borderRightWidth: 1,
          paddingHorizontal: scale(12),
          backgroundColor: colors.grey1,
          borderRightColor: colors.grey3,
          alignItems: 'center',
          justifyContent: 'center',
          borderTopLeftRadius: 6,
          borderBottomLeftRadius: 6,
          marginRight: 1,
        },
        labelText: {
          fontSize: scale(14),
          color: colors.grey6,
        },
        errorText: {
          paddingTop: scale(6),
          paddingLeft: scale(12),
          color: colors.red,
        },
        accessory: {
          width: scale(42),
          height: scale(42),
          position: 'absolute',
          right: 1,
          bottom: 1,
          alignItems: 'center',
          justifyContent: 'center',
          borderTopRightRadius: theme.radius.s,
          borderBottomRightRadius: theme.radius.s,
        },
        colour: {
          width: 'auto',
          paddingRight: scale(6),
          flexDirection: 'row',
        },
        swatch: {
          width: scale(30),
          height: scale(22),
          marginRight: scale(4),
          borderRadius: theme.radius.s,
          backgroundColor: colors.black,
        },
      }),
    [colors, scale],
  );
};

export default InputTextStyles;
